import { Injectable } from '@angular/core';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { DirectoryHistoryServiceInterface } from '@zaoblako/shared/common/interfaces/directory-history-service.interface';
import type { DirectionAddUserFormInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-add-user-form.dto';
import type { DirectionAddUserGroupFormInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-add-user-group-form.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { DirectionGroupFormInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-group-form.dto';
import type { DirectionQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-query.dto';
import type { DirectionsReorderInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/directions-reorder.dto';
import type { DirectionCategoryFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-category-find.dto';
import type { DirectionItemsPageInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-items-page.dto';
import type { DirectionRegistryQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-registry-query.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';
import { DirectionBulkAssignFinancialClassificationInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-bulk-assign-financial-classification.dto';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';

@Injectable({
  providedIn: 'root'
})
export class DirectionService extends BaseService implements DirectoryHistoryServiceInterface {
  protected baseUrl = '/basis/catalog/direction';

  public override find<T>(queryParams?: DirectionQueryInterface): Observable<T[]> {
    const params = queryParams ? this.objectToHttpParams(queryParams) : {};
    return this.http.get<T[]>(`${this.baseUrl}/tree`, { params });
  }

  public getReportDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(this.baseUrl);
  }

  public findPage(queryParams?: DirectionRegistryQueryInterface): Observable<DirectionItemsPageInterface> {
    const params = queryParams ? this.objectToHttpParams(queryParams) : {};
    return this.http.get<DirectionItemsPageInterface>(`${this.baseUrl}/items`, { params });
  }

  public flat(queryParams?: DirectionQueryInterface): Observable<DirectionFindInterface[]> {
    const params = queryParams ? this.objectToHttpParams(queryParams) : {};
    return this.http.get<DirectionFindInterface[]>(`${this.baseUrl}/flat`, { params });
  }

  public history(queryParams: PaginationRequiredQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public createGroup(directionGroup: DirectionGroupFormInterface): Observable<DirectionFindInterface> {
    return this.http.post<DirectionFindInterface>(`${this.baseUrl}/group`, directionGroup);
  }

  public synchronize1cAll(): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/synchronize-1c`);
  }

  public synchronizeAtriumAll(): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/synchronize-atrium`);
  }

  public restore(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/restore`, {});
  }

  public getLeafDirections<T extends { children?: any[] }>(directions: T[]): T[] {
    let leafDirections = [];

    directions?.forEach((direction) => {
      if (direction.children?.length) {
        leafDirections.push(...this.getLeafDirections(direction.children));
      } else {
        leafDirections.push(direction);
      }
    });

    return [...new Set(leafDirections)];
  }

  public getLeafDirectionsWithParent<T extends { children?: any[] }>(directions: T[]): T[] {
    let leafDirections = [];

    const parseDirections = (_directions: T[]) => {
      _directions?.forEach((direction) => {
        leafDirections.push(direction);
        if (direction.children?.length) {
          parseDirections(direction.children);
        }
      });
    };

    parseDirections(directions);
    return [...new Set(leafDirections)];
  }

  public getRootDirections<T extends { id: number; parent?: T }>(directions: T[]): T[] {
    const rootDirections = (directions || [])?.filter(
      (direction) => directions?.findIndex((_direction) => _direction.id === direction.parent?.id) === -1
    );
    return [...new Set(rootDirections)];
  }

  public findDirectionById<T extends { id: number; children?: any[] }>(directions: T[], targetId: number): T {
    for (const direction of directions) {
      if (direction.id === targetId) {
        return direction;
      }

      if (direction.children?.length) {
        const foundInChildren = this.findDirectionById(direction.children, targetId);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  }

  public findDirectionWithAllParents<T extends { id: number; parent?: T; children?: any[] }>(
    directions: T[],
    targetId: number
  ): T[] {
    const foundDirection: T = this.findDirectionById(directions, targetId);
    if (!foundDirection) {
      return [];
    }

    const chain: T[] = [];
    let currentDirection: T = foundDirection;

    while (currentDirection) {
      chain.unshift(currentDirection);
      currentDirection = this.findDirectionById(directions, currentDirection.parent?.id);
    }
    return chain;
  }

  public getHistoryForOne(id: number, queryParams?: PaginationQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public addUser(id: number, body: DirectionAddUserFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/add-user`, body);
  }

  public addUserGroup(id: number, body: DirectionAddUserGroupFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/add-user-group`, body);
  }

  public reorderDirections(body: DirectionsReorderInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/reorder`, body);
  }

  public getItemKinds(id: number): Observable<DirectionKindEnum[]> {
    return this.http.get<DirectionKindEnum[]>(`${this.baseUrl}/${id}/items-kinds`);
  }

  public assignFinancialClassification(
    id: number,
    body: DirectionBulkAssignFinancialClassificationInterface
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/assign-financial-classification`, body);
  }

  public financialClassification(queryParams: DirectionQueryInterface): Observable<DirectionCategoryFindInterface[]> {
    return this.http.get<DirectionCategoryFindInterface[]>(`${this.baseUrl}/financial-classification`, {
      params: this.objectToHttpParams(queryParams)
    });
  }
}
